import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Palette,
  Settings,
  Eye,
  Monitor,
  Smartphone,
  ExternalLink,
  Layers,
  Undo2,
  Redo2,
} from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import TemplateSelector from './components/TemplateSelector';
import ColorSchemeSelector from './components/ColorSchemeSelector';
import CustomizePanel from './components/CustomizePanel';
import PreviewPanel from './components/PreviewPanel';
import TitleColorPicker from './components/TitleColorPicker';
import SaveButton from './components/SaveButton';
import {
  getEventData,
  setEventData,
  getSelectedTemplateId,
  setSelectedTemplateId,
  clearEventData,
} from './StorageHelper';
import useCustomFecthQuery from '../../../Hooks/useCustomFecthQuery';
import { useEventData } from './hooks/useEventData';
import { useBuilderState } from './hooks/useBuilderState';
import { useAutoSave } from './hooks/useAutoSave';
import { useLandingPageSync } from './hooks/useLandingPageSync';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useUnsavedChangesWarning } from './hooks/useUnsavedChangesWarning';
import { transformEventDataFromBackend } from './utils/dataTransformers';
import { validateField } from './utils/validators';
import { TEMPLATES, COLOR_SCHEMES, DEFAULT_EVENT_DATA } from './utils/constants';
import { warningMessage } from '../../../components/Toast/Toast';
import './EventLandingBuilderStylesTwo.css';

const EventLandingBuilder = ({ eventDetails, token, userID }) => {
  const eventID = eventDetails?._id || eventDetails?.id;

  // Builder state management
  const [state, dispatch] = useBuilderState();
  
  // Track if we've loaded data from database to prevent re-loading
  const hasLoadedFromDatabaseRef = useRef(false);
  
  // Track if we've successfully saved to backend at least once
  const hasSavedToBackendRef = useRef(false);

  // ⏱️ SESSION TIMEOUT STATE
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  
  const navigate = (path) => {
    window.location.href = path;
  };

  // Load landing page config from API
  const {
    data: landingPageData,
    isLoading: isLoadingLandingPage,
    refetch: refetchLandingPage,
  } = useCustomFecthQuery(
    ['eventLandingPage', eventID],
    `event-landing-pages/${eventID}/event`,
    token,
    { enabled: !!eventID && !!token }
  );

  // Load registration form from API
  const {
    data: registrationFormData,
    isLoading: isLoadingRegistrationForm,
  } = useCustomFecthQuery(
    ['eventForm', eventID],
    `event-form/${eventID}`,
    null,
    { enabled: !!eventID }
  );

 // In getInitialEventData function - ADD venueMap:
const getInitialEventData = useCallback(() => {
  if (eventDetails) {
    return {
      title: eventDetails.eventName || DEFAULT_EVENT_DATA.title,
      subtitle:
        eventDetails.eventDescription?.substring(0, 60) ||
        DEFAULT_EVENT_DATA.subtitle,
      description:
        eventDetails.eventDescription || DEFAULT_EVENT_DATA.description,
      date: eventDetails.startDate || DEFAULT_EVENT_DATA.date,
      time: eventDetails.startTime || DEFAULT_EVENT_DATA.time,
      location:
        eventDetails.location ||
        eventDetails.venue ||
        DEFAULT_EVENT_DATA.location,
      venueMap: eventDetails.venueMap || '',  // ✅ ADDED: venueMap field
      capacity:
        eventDetails.capacity ||
        eventDetails.expectedAttendees ||
        DEFAULT_EVENT_DATA.capacity,
      ctaText: DEFAULT_EVENT_DATA.ctaText,
      features: [...DEFAULT_EVENT_DATA.features],
      speakers: [...DEFAULT_EVENT_DATA.speakers],
      videoLink: eventDetails.videoLink || DEFAULT_EVENT_DATA.videoLink,
      supportMail: eventDetails.supportMail || '',
      supportPhoneNumber: eventDetails.supportPhoneNumber || '',
    };
  }
  return { ...DEFAULT_EVENT_DATA };
}, [eventDetails]);

  // Event data management with undo/redo
  const initialData = useMemo(() => getInitialEventData(), [eventDetails]);
  const {
    eventData = {},
    updateField,
    undo,
    redo,
    canUndo,
    canRedo,
    hasChanges,
  } = useEventData(initialData);

  // API sync
  const { save, isLoading: isSaving } = useLandingPageSync(eventID, token);

  // Auto-save functionality - ALWAYS save to backend first
  const handleAutoSave = useCallback(
    async (data) => {
      if (!state.selectedTemplate || !token) return;
      
      const colorSchemeIndex = COLOR_SCHEMES.findIndex(
        (scheme) => scheme.id === state.selectedColorScheme
      );
      
      try {
        // ✅ ALWAYS save to backend first
        await save(data, state.selectedTemplate, colorSchemeIndex >= 0 ? colorSchemeIndex : 0);
        dispatch({ type: 'MARK_SAVED' });
        hasSavedToBackendRef.current = true;
        
        // ✅ Then save to localStorage as backup cache
        if (eventID) {
          const dataToSave = {
            ...data,
            templateId: state.selectedTemplate.id,
            colorScheme: {
              id: state.selectedColorScheme,
              index: colorSchemeIndex >= 0 ? colorSchemeIndex : 0,
            },
          };
          setEventData(eventID, dataToSave);
          setSelectedTemplateId(state.selectedTemplate.id);
        }
      } catch (error) {
        console.error('Auto-save to backend failed:', error);
        // Fallback: save to localStorage only
        if (eventID) {
          const dataToSave = {
            ...data,
            templateId: state.selectedTemplate?.id,
            colorScheme: {
              id: state.selectedColorScheme,
              index: colorSchemeIndex >= 0 ? colorSchemeIndex : 0,
            },
          };
          setEventData(eventID, dataToSave);
        }
      }
    },
    [state.selectedTemplate, state.selectedColorScheme, save, dispatch, eventID, token]
  );

  useAutoSave(eventData, eventID, handleAutoSave, 2000, !!state.selectedTemplate && !!token);

  // Mark changes when eventData updates
  useEffect(() => {
    if (!eventID) return;
    dispatch({ type: 'MARK_CHANGED' });
  }, [eventData, eventID, dispatch]);

  // ===== SESSION TIMEOUT LOGIC =====
  useEffect(() => {
    console.log('⏱️ LandingBuilder: Session timeout check - token prop exists:', !!token);
    
    // ✅ Use token PROP instead of localStorage
    if (!token) {
      console.warn('⚠️ LandingBuilder: Session timeout: No token prop - skipping');
      return;
    }

    // ⏱️ PRODUCTION CONFIG: 15 minutes total (13m idle + 2m warning)
    const IDLE_TIMEOUT = 10 * 60 * 1000;  // 15 minutes (900000ms)
    const WARNING_TIME = 2 * 60 * 1000;    // 2 minute warning (120000ms)
    
    console.log(`✅ LandingBuilder: Session timeout started - Idle: ${IDLE_TIMEOUT/1000/60}min, Warning: ${WARNING_TIME/1000/60}min`);

    const resetIdleTimer = () => {
      // ⚠️ IMPORTANT: Only reset if warning is NOT showing
      // Once warning appears, mouse movement should NOT dismiss it
      if (showSessionWarning) {
        return; // Don't reset timers while warning is active
      }

      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);

      // Start warning timer (show warning after idle period)
      warningTimerRef.current = setTimeout(() => {
        console.log('⚠️ LandingBuilder: Session expiring in 2 minutes!');
        setShowSessionWarning(true);

        // Force logout after warning period (user must click a button)
        idleTimerRef.current = setTimeout(() => {
          console.log('🚪 LandingBuilder: Auto-logging out after warning period...');
          performLogout();
        }, WARNING_TIME);
      }, IDLE_TIMEOUT - WARNING_TIME);
    };

    const performLogout = () => {
      console.log('🚪 LandingBuilder: Logging out user...');
      // Clear localStorage just in case
      const keys = ['token', 'authToken', 'accessToken', 'user', 'authEventKagoUser'];
      keys.forEach(key => localStorage.removeItem(key));
      
      navigate('/login');
    };

    // Listen for user activity (only resets timer if warning is not showing)
    const events = ['mousedown', 'keydown', 'scroll', 'mousemove', 'click', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer, { passive: true }));

    // Start initial timer
    resetIdleTimer();

    // Cleanup
    return () => {
      console.log('🧹 LandingBuilder: Session timeout: Cleaning up listeners and timers');
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [token, showSessionWarning]); // ✅ Add showSessionWarning as dependency
  // ===== END SESSION TIMEOUT =====

  // Get color scheme index helper
  const getColorSchemeIndex = useCallback(() => {
    const index = COLOR_SCHEMES.findIndex(
      (scheme) => scheme.id === state.selectedColorScheme
    );
    return index >= 0 ? index : 0;
  }, [state.selectedColorScheme]);

  // Ensure we persist before unload - save to backend AND localStorage
  const handleBeforeUnload = useCallback(() => {
    if (!eventID || !state.selectedTemplate) return;

    try {
      const colorSchemeIndex = getColorSchemeIndex();

      const dataToSave = {
        ...eventData,
        templateId: state.selectedTemplate.id,
        colorScheme: {
          id: state.selectedColorScheme,
          index: colorSchemeIndex,
        },
      };

      // ✅ Save to localStorage as immediate backup
      setEventData(eventID, dataToSave);
      setSelectedTemplateId(state.selectedTemplate.id);
      
      // ✅ Try to save to backend (async, won't block unload)
      if (token) {
        save(eventData, state.selectedTemplate, colorSchemeIndex)
          .then(() => {
            hasSavedToBackendRef.current = true;
            dispatch({ type: 'MARK_SAVED' });
          })
          .catch((error) => {
            console.error('Beforeunload save failed:', error);
          });
      }
    } catch (e) {
      console.error('Failed to persist landing page data on unload:', e);
    }
  }, [eventID, state.selectedTemplate, state.selectedColorScheme, eventData, getColorSchemeIndex, save, token, dispatch]);

  // Unsaved changes warning + last-chance persistence
  useUnsavedChangesWarning(state.hasUnsavedChanges, handleBeforeUnload);

  // Save handler - ALWAYS save to backend
  const handleSave = useCallback(async () => {
    if (!state.selectedTemplate) {
      warningMessage('Please select a template before saving.');
      return;
    }

    if (!token) {
      warningMessage('Authentication required to save. Please log in.');
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const colorSchemeIndex = getColorSchemeIndex();
      
      // ✅ Save to backend
      await save(eventData, state.selectedTemplate, colorSchemeIndex);
      dispatch({ type: 'MARK_SAVED' });
      hasSavedToBackendRef.current = true;
      
      // ✅ Also update localStorage as backup
      if (eventID) {
        const dataToSave = {
          ...eventData,
          templateId: state.selectedTemplate.id,
          colorScheme: {
            id: state.selectedColorScheme,
            index: colorSchemeIndex,
          },
        };
        setEventData(eventID, dataToSave);
        setSelectedTemplateId(state.selectedTemplate.id);
      }

      // Refetch to get updated data
      await new Promise((resolve) => setTimeout(resolve, 500));
      await refetchLandingPage();
      
    } catch (error) {
      console.error('Save error:', error);
      warningMessage('Failed to save to server. Changes saved locally only.');
      
      // Fallback: save to localStorage
      if (eventID) {
        const colorSchemeIndex = getColorSchemeIndex();
        const dataToSave = {
          ...eventData,
          templateId: state.selectedTemplate.id,
          colorScheme: {
            id: state.selectedColorScheme,
            index: colorSchemeIndex,
          },
        };
        setEventData(eventID, dataToSave);
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    state.selectedTemplate,
    eventData,
    save,
    getColorSchemeIndex,
    refetchLandingPage,
    dispatch,
    eventID,
    token,
  ]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSave: handleSave,
    onUndo: undo,
    onRedo: redo,
    onCancel: () => dispatch({ type: 'HIDE_CONFIRM_MODAL' }),
  });

  // Reset load flag when eventID changes
  useEffect(() => {
    hasLoadedFromDatabaseRef.current = false;
    hasSavedToBackendRef.current = false;
  }, [eventID]);

  // Load data: Backend FIRST, then localStorage as fallback
  useEffect(() => {
    if (!eventID || isLoadingLandingPage) return;
    
    if (hasLoadedFromDatabaseRef.current) return;

    // ✅ Priority 1: Load from backend API (source of truth)
    if (landingPageData?.data) {
      const apiData = Array.isArray(landingPageData.data) 
        ? landingPageData.data[0] 
        : landingPageData.data;
      
      if (apiData) {
        let loadedTemplate = null;

        // Restore template
        if (apiData.templateId) {
          const template = TEMPLATES.find((t) => t.id === apiData.templateId);
          if (template) {
            loadedTemplate = template;
            dispatch({ type: 'SELECT_TEMPLATE', payload: template });
            dispatch({ type: 'SET_TAB', payload: 'customize' });
          }
        }

        // Restore event data
        if (apiData.eventData) {
          const frontendData = transformEventDataFromBackend(apiData.eventData);
          if (frontendData) {
            if (!frontendData.bannerImage && loadedTemplate?.preview) {
              frontendData.bannerImage = loadedTemplate.preview;
            }
            Object.keys(frontendData).forEach((key) => {
              updateField(key, frontendData[key]);
            });
          }
        }

        // Restore color scheme
        if (
          apiData.colorSchemeIndex !== undefined &&
          apiData.colorSchemeIndex !== null
        ) {
          const schemeIndex =
            typeof apiData.colorSchemeIndex === 'number'
              ? apiData.colorSchemeIndex
              : parseInt(apiData.colorSchemeIndex, 10);
          if (
            !isNaN(schemeIndex) &&
            schemeIndex >= 0 &&
            schemeIndex < COLOR_SCHEMES.length
          ) {
            dispatch({
              type: 'SET_COLOR_SCHEME',
              payload: COLOR_SCHEMES[schemeIndex].id,
            });
          }
        } else if (apiData.colorScheme?.id) {
          dispatch({
            type: 'SET_COLOR_SCHEME',
            payload: apiData.colorScheme.id,
          });
        }

        hasLoadedFromDatabaseRef.current = true;
        hasSavedToBackendRef.current = true; // Mark as synced
        return;
      }
    }

    // ✅ Priority 2: Fallback to localStorage ONLY if backend has no data
    const savedEventData = getEventData(eventID);
    if (savedEventData && savedEventData.title) {
      const { templateId, colorScheme, ...cleanEventData } = savedEventData;
      
      // Restore event data
      Object.keys(cleanEventData).forEach((key) => {
        updateField(key, cleanEventData[key]);
      });

      // Restore color scheme
      if (colorScheme?.id) {
        dispatch({ type: 'SET_COLOR_SCHEME', payload: colorScheme.id });
      }

      // Restore template
      if (templateId) {
        const template = TEMPLATES.find((t) => t.id === templateId);
        if (template) {
          dispatch({ type: 'SELECT_TEMPLATE', payload: template });
          dispatch({ type: 'SET_TAB', payload: 'customize' });
          setSelectedTemplateId(templateId);

          if (!cleanEventData.bannerImage && template.preview) {
            updateField('bannerImage', template.preview);
          }
        }
      }
    } else {
      // Last resort: restore just template ID
      const savedTemplateId = getSelectedTemplateId();
      if (savedTemplateId) {
        const template = TEMPLATES.find((t) => t.id === savedTemplateId);
        if (template) {
          dispatch({ type: 'SELECT_TEMPLATE', payload: template });
          dispatch({ type: 'SET_TAB', payload: 'customize' });
        }
      }
    }
    
    hasLoadedFromDatabaseRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventID, landingPageData, isLoadingLandingPage, token]);

  // Sync eventDetails changes to eventData for preview
  useEffect(() => {
    if (!eventDetails) return;
    
    const updates = {};
    
    if (eventDetails.eventName && eventDetails.eventName !== eventData.title) {
      updates.title = eventDetails.eventName;
    }
    
    if (eventDetails.eventDescription) {
      if (eventDetails.eventDescription !== eventData.description) {
        updates.description = eventDetails.eventDescription;
      }
      const newSubtitle = eventDetails.eventDescription.substring(0, 60);
      if (!eventData.subtitle || eventData.subtitle === newSubtitle) {
        updates.subtitle = newSubtitle;
      }
    }
    
    if (eventDetails.startDate && eventDetails.startDate !== eventData.date) {
      updates.date = eventDetails.startDate;
    }
    
    if (eventDetails.startTime && eventDetails.startTime !== eventData.time) {
      updates.time = eventDetails.startTime;
    }
    
    if (eventDetails.location && eventDetails.location !== eventData.location) {
      updates.location = eventDetails.location;
    }
    
    if (eventDetails.venue && eventDetails.venue !== eventData.location) {
      updates.location = eventDetails.venue;
    }
    
    if (eventDetails.bannerImage && eventDetails.bannerImage !== eventData.bannerImage) {
      updates.bannerImage = eventDetails.bannerImage;
    }
    
    if (eventDetails.logo && eventDetails.logo !== eventData.logoImage) {
      updates.logoImage = eventDetails.logo;
    }
    
    if (eventDetails.videoLink && eventDetails.videoLink !== eventData.videoLink) {
      updates.videoLink = eventDetails.videoLink;
    }
    
    if (eventDetails.supportMail !== undefined && eventDetails.supportMail !== eventData.supportMail) {
      updates.supportMail = eventDetails.supportMail || '';
    }
    
    if (eventDetails.supportPhoneNumber !== undefined && eventDetails.supportPhoneNumber !== eventData.supportPhoneNumber) {
      updates.supportPhoneNumber = eventDetails.supportPhoneNumber || '';
    }
    
    if (Object.keys(updates).length > 0) {
      Object.keys(updates).forEach((key) => {
        if (updates[key] !== undefined && updates[key] !== null) {
          updateField(key, updates[key]);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDetails?.eventName, eventDetails?.eventDescription, eventDetails?.startDate, 
      eventDetails?.startTime, eventDetails?.location, eventDetails?.venue, 
      eventDetails?.bannerImage, eventDetails?.logo, eventDetails?.videoLink, 
      eventDetails?.supportMail, eventDetails?.supportPhoneNumber,  eventDetails?.venueMap, updateField]);

  // ✅ Save to localStorage as backup cache (not primary storage)
  useEffect(() => {
    if (state.selectedTemplate && eventID && hasSavedToBackendRef.current) {
      const dataToSave = {
        ...eventData,
        templateId: state.selectedTemplate.id,
        colorScheme: getSelectedColorScheme(),
      };
      setEventData(eventID, dataToSave);
    }
  }, [eventData, state.selectedTemplate, state.selectedColorScheme, eventID, hasSavedToBackendRef]);

  // Memoized color scheme
  const selectedColorSchemeObj = useMemo(() => {
    return (
      COLOR_SCHEMES.find((scheme) => scheme.id === state.selectedColorScheme) ||
      COLOR_SCHEMES[0]
    );
  }, [state.selectedColorScheme]);

  const getSelectedColorScheme = () => {
    return selectedColorSchemeObj;
  };

  // Template selection handlers
  const handleTemplateSelect = useCallback(
    (template, needsConfirmation) => {
      if (needsConfirmation && state.selectedTemplate) {
        dispatch({ type: 'SHOW_CONFIRM_MODAL', payload: template });
      } else if (template) {
        selectTemplate(template);
      } else {
        dispatch({ type: 'SELECT_TEMPLATE', payload: null });
        setSelectedTemplateId('');
        clearEventData(eventID);
        dispatch({ type: 'SET_TAB', payload: 'templates' });
      }
    },
    [state.selectedTemplate, eventID, dispatch]
  );

  const selectTemplate = useCallback(
    (template) => {
      dispatch({ type: 'SELECT_TEMPLATE', payload: template });
      setSelectedTemplateId(template.id);
      dispatch({ type: 'SET_TAB', payload: 'customize' });

      const savedEventData = getEventData(eventID);
      if (
        savedEventData &&
        savedEventData.title &&
        savedEventData.templateId === template.id
      ) {
        const { templateId, colorScheme, ...cleanEventData } = savedEventData;
        if (!cleanEventData.bannerImage && template.preview) {
          cleanEventData.bannerImage = template.preview;
        }
        Object.keys(cleanEventData).forEach((key) => {
          updateField(key, cleanEventData[key]);
        });
        if (colorScheme?.id) {
          dispatch({ type: 'SET_COLOR_SCHEME', payload: colorScheme.id });
        }
      } else {
        const defaultData = eventDetails
          ? {
              title: eventDetails.eventName || DEFAULT_EVENT_DATA.title,
              subtitle:
                eventDetails.eventDescription?.substring(0, 60) ||
                DEFAULT_EVENT_DATA.subtitle,
              description:
                eventDetails.eventDescription || DEFAULT_EVENT_DATA.description,
              date: eventDetails.startDate || DEFAULT_EVENT_DATA.date,
              time: eventDetails.startTime || DEFAULT_EVENT_DATA.time,
              location:
                eventDetails.location ||
                eventDetails.venue ||
                DEFAULT_EVENT_DATA.location,
              capacity:
                eventDetails.capacity ||
                eventDetails.expectedAttendees ||
                DEFAULT_EVENT_DATA.capacity,
              ctaText: DEFAULT_EVENT_DATA.ctaText,
              bannerImage: template.preview || '',
              features: [...DEFAULT_EVENT_DATA.features],
              speakers: [...DEFAULT_EVENT_DATA.speakers],
            }
          : {
              ...DEFAULT_EVENT_DATA,
              bannerImage: template.preview || '',
            };
        Object.keys(defaultData).forEach((key) => {
          updateField(key, defaultData[key]);
        });
      }
    },
    [eventID, eventDetails, updateField, dispatch]
  );

  const handleConfirmTemplateSwitch = useCallback(() => {
    if (state.pendingTemplate) {
      clearEventData(eventID);
      selectTemplate(state.pendingTemplate);
    }
    dispatch({ type: 'HIDE_CONFIRM_MODAL' });
  }, [state.pendingTemplate, eventID, selectTemplate, dispatch]);

  // Field update with validation
  const handleFieldUpdate = useCallback(
    (field, value) => {
      updateField(field, value);
      dispatch({ type: 'MARK_CHANGED' });

      const error = validateField(field, value);
      if (error) {
        dispatch({ type: 'SET_ERROR', field, message: error });
      } else {
        dispatch({ type: 'CLEAR_ERROR', field });
      }
    },
    [updateField, dispatch]
  );

  const handleFieldBlur = useCallback(
    (field, value) => {
      const error = validateField(field, value);
      if (error) {
        dispatch({ type: 'SET_ERROR', field, message: error });
      } else {
        dispatch({ type: 'CLEAR_ERROR', field });
      }
    },
    [dispatch]
  );

  // Preview handler
  const handlePreview = useCallback(() => {
    if (!state.selectedTemplate) {
      warningMessage('Please select a template before previewing.');
      return;
    }

    if (!eventID) {
      warningMessage('Event ID not found. Cannot preview.');
      return;
    }

    const previewUrl = `/event-landing-pages/${eventID}/preview`;
    window.open(previewUrl, '_blank', 'noopener,noreferrer');

    (async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const colorSchemeIndex = getColorSchemeIndex();
        await save(eventData, state.selectedTemplate, colorSchemeIndex);
        dispatch({ type: 'MARK_SAVED' });
        hasSavedToBackendRef.current = true;
        await new Promise((resolve) => setTimeout(resolve, 300));
      } catch (error) {
        console.error('Preview save error:', error);
        warningMessage(
          'Failed to save latest changes before preview. Showing last saved version.'
        );
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    })();
  }, [state.selectedTemplate, eventID, eventData, save, getColorSchemeIndex, dispatch]);

  // ===== SESSION TIMEOUT WARNING MODAL HANDLERS =====
  const handleStayLoggedIn = () => {
    console.log('🖱️ LandingBuilder: User clicked "Stay Logged In"');
    setShowSessionWarning(false);
    // Timer will auto-reset via the useEffect dependency on showSessionWarning
  };

  const handleLogoutNow = () => {
    console.log('🚪 LandingBuilder: User clicked "Log Out Now"');
    setShowSessionWarning(false);
    const keys = ['token', 'authToken', 'accessToken', 'user', 'authEventKagoUser'];
    keys.forEach(key => localStorage.removeItem(key));
    navigate('/login');
  };

  // Render templates tab
  const renderTemplatesTab = () => (
    <TemplateSelector
      templates={TEMPLATES}
      selectedTemplate={state.selectedTemplate}
      onSelect={handleTemplateSelect}
      onPreview={handlePreview}
    />
  );

  // Render color schemes & typography colors tab
  const renderColorSchemesTab = () => (
    <div className="landing-colors-tab">
      <div className="landing-section-primary">
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <Palette size={18} className="landing-section-icon" />
            <div>
              <h3 className="landing-section-title">Color Palette</h3>
              <p className="landing-section-description">
                Choose a base color scheme for your event landing page.
              </p>
            </div>
          </div>
        </div>
        <ColorSchemeSelector
          colorSchemes={COLOR_SCHEMES}
          selectedColorScheme={state.selectedColorScheme}
          onSelect={(schemeId) =>
            dispatch({ type: 'SET_COLOR_SCHEME', payload: schemeId })
          }
        />
      </div>

      <div className="landing-section-primary">
        <div className="landing-section-header">
          <div className="landing-section-header-content">
            <Layers size={18} className="landing-section-icon" />
            <div>
              <h3 className="landing-section-title">Typography</h3>
              <p className="landing-section-description">
                Fine-tune key text colors to match your brand.
              </p>
            </div>
          </div>
        </div>

        <div className="landing-form-group">
          <label className="landing-label">
            <span>Event Title Color</span>
          </label>
          <TitleColorPicker
            value={eventData.eventTitleColor || ''}
            onChange={(newColor) => handleFieldUpdate('eventTitleColor', newColor)}
          />
          <span className="landing-hint">
            This controls the big hero title text color on your landing page. Use a
            high-contrast color so it remains readable on top of the banner image.
          </span>
        </div>
      </div>
    </div>
  );

  // Render customize tab
  const renderCustomizeTab = () => {
    if (!state.selectedTemplate) {
      return (
        <div className="landing-customize-container">
          <div className="landing-no-template">
            <Settings size={48} />
            <h3>No Template Selected</h3>
            <p>
              Please select a template from the Templates tab to start
              customizing your event landing page.
            </p>
            <div className="landing-preview-controls">
              <button
                className="landing-button landing-button-primary"
                onClick={handlePreview}
                disabled={!state.selectedTemplate}
              >
                <Eye size={16} />
                Preview in New Tab
              </button>
              <button
                className="landing-button landing-button-primary"
                onClick={() => dispatch({ type: 'SET_TAB', payload: 'templates' })}
              >
                Browse Templates
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="landing-customize-container">
        <div className="landing-customize-header">
          <div className="landing-template-selected">
            <h3>{state.selectedTemplate.name}</h3>
            <p>Customize your event landing page</p>
          </div>
          <div className="landing-preview-controls">
            <div className="landing-device-toggle">
              <button
                className={`landing-device-btn ${
                  state.previewDevice === 'desktop' ? 'active' : ''
                }`}
                onClick={() =>
                  dispatch({ type: 'SET_PREVIEW_DEVICE', payload: 'desktop' })
                }
              >
                <Monitor size={14} />
                Desktop
              </button>
              <button
                className={`landing-device-btn ${
                  state.previewDevice === 'mobile' ? 'active' : ''
                }`}
                onClick={() =>
                  dispatch({ type: 'SET_PREVIEW_DEVICE', payload: 'mobile' })
                }
              >
                <Smartphone size={14} />
                Mobile
              </button>
            </div>

            <button
              className="landing-button landing-button-outline"
              onClick={handlePreview}
            >
              <ExternalLink size={16} />
              Open Preview
            </button>

            <SaveButton
              onSave={handleSave}
              isLoading={isSaving}
              hasUnsavedChanges={state.hasUnsavedChanges}
            />

            {/* Undo/Redo buttons */}
            <div className="landing-undo-redo">
              <button
                className="landing-button landing-button-icon"
                onClick={undo}
                disabled={!canUndo}
                aria-label="Undo"
                title="Undo (Ctrl+Z)"
              >
                <Undo2 size={16} />
              </button>
              <button
                className="landing-button landing-button-icon"
                onClick={redo}
                disabled={!canRedo}
                aria-label="Redo"
                title="Redo (Ctrl+Shift+Z)"
              >
                <Redo2 size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="landing-customize-content">
          <CustomizePanel
            eventData={eventData || {}}
            onUpdate={handleFieldUpdate}
            errors={state.errors || {}}
            onFieldBlur={handleFieldBlur}
            eventDetails={eventDetails}
            token={token}
            userID={userID}
          />

          <PreviewPanel
            eventData={eventData || {}}
            colorScheme={selectedColorSchemeObj}
            previewDevice={state.previewDevice}
            registrationForm={registrationFormData?.data?.[0]?.customFields}
          />
        </div>
      </div>
    );
  };

  if (isLoadingLandingPage) {
    return (
      <div className="landing-body">
        <div className="landing-loading">
          <div className="spinner" />
          <p>Loading landing page configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-body">
      
      {/* ⏱️ SESSION TIMEOUT WARNING MODAL - STAYS UNTIL USER CLICKS */}
      {showSessionWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', padding: '40px 32px', borderRadius: '16px',
            maxWidth: '450px', width: '90%', textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{
              width: '80px', height: '80px', background: '#fff7ed',
              borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 24px'
            }}>
              <span style={{ fontSize: '40px' }}>⏰</span>
            </div>
            
            <h2 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '24px', fontWeight: '700' }}>
              Session Expiring Soon
            </h2>
            
            <p style={{ color: '#64748b', marginBottom: '32px', fontSize: '16px', lineHeight: 1.5 }}>
              For your security, you'll be logged out in <strong style={{ color: '#f97316' }}>2 minutes</strong> due to inactivity.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* ✅ TWO BUTTONS AS REQUESTED */}
              <button
                onClick={handleLogoutNow}
                style={{
                  padding: '12px 24px', background: '#f1f5f9', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#475569',
                  fontWeight: '600', fontSize: '14px', transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                Log Out Now
              </button>
              
              <button
                onClick={handleStayLoggedIn}
                style={{
                  padding: '12px 24px', background: 'linear-gradient(135deg, #FF7A00 0%, #ff8560f0 100%)',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff',
                  fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 14px rgba(255, 122, 0, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 122, 0, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 122, 0, 0.4)';
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
          
          <style>{`
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(-20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      <ConfirmModal
        isOpen={state.showConfirmModal}
        onClose={() => dispatch({ type: 'HIDE_CONFIRM_MODAL' })}
        onConfirm={handleConfirmTemplateSwitch}
        title="Switch Template?"
        message="Deactivating the current template will reset your customizations. Are you sure you want to continue?"
      />

      <div className="landing-body--container">
        <div className="landing-header">
          <h1>Event Landing Page Builder</h1>
          <p>Create stunning landing pages for your events in minutes</p>
        </div>

        <div className="landing-tabs" role="tablist">
          <button
            className={`landing-tab ${
              state.activeTab === 'templates' ? 'active' : ''
            }`}
            onClick={() => dispatch({ type: 'SET_TAB', payload: 'templates' })}
            role="tab"
            aria-selected={state.activeTab === 'templates'}
            aria-controls="templates-panel"
          >
            <Layers size={16} />
            Templates
          </button>
          <button
            className={`landing-tab ${
              state.activeTab === 'colors' ? 'active' : ''
            }`}
            onClick={() => dispatch({ type: 'SET_TAB', payload: 'colors' })}
            role="tab"
            aria-selected={state.activeTab === 'colors'}
            aria-controls="colors-panel"
          >
            <Palette size={16} />
            Colors
          </button>
          <button
            className={`landing-tab ${
              state.activeTab === 'customize' ? 'active' : ''
            }`}
            onClick={() =>
              dispatch({ type: 'SET_TAB', payload: 'customize' })
            }
            role="tab"
            aria-selected={state.activeTab === 'customize'}
            aria-controls="customize-panel"
          >
            <Settings size={16} />
            Customize
          </button>
        </div>

        <div className="landing-content">
          {state.activeTab === 'templates' && (
            <div
              id="templates-panel"
              role="tabpanel"
              aria-labelledby="templates-tab"
            >
              {renderTemplatesTab()}
            </div>
          )}
          {state.activeTab === 'colors' && (
            <div
              id="colors-panel"
              role="tabpanel"
              aria-labelledby="colors-tab"
            >
              {renderColorSchemesTab()}
            </div>
          )}
          {state.activeTab === 'customize' && (
            <div
              id="customize-panel"
              role="tabpanel"
              aria-labelledby="customize-tab"
            >
              {renderCustomizeTab()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventLandingBuilder;